import {
  Backdrop,
  Box,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  styled,
  Autocomplete,
  Button,
  TextField,
  DialogTitle,
  DialogContent,
  Dialog,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { SimpleCard } from "app/components";
import CustomButton from "app/components/Button";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  createDepartment,
  deleteDepartment,
  getAllDepartment,
  updateDepartment,
} from "app/services/Services";
import { generateErrorMessage, generateSuccessMessage } from "app/utils/GenerateMessage";
import { convertedDate } from "app/utils/utils";
import BackdropLoader from "app/components/BackdropLoader";
import { debounce } from "lodash";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmModal from "../employee-details/ConfirmModal";

const Container = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: { margin: "16px" },
  "& .breadcrumb": {
    marginBottom: "30px",
    [theme.breakpoints.down("sm")]: { marginBottom: "16px" },
  },
}));

const StyledTable = styled(Table)(() => ({
  whiteSpace: "pre",
  "& thead": {
    "& tr": { "& th": { paddingLeft: 0, paddingRight: 0 } },
  },
  "& tbody": {
    "& tr": { "& td": { paddingLeft: 0, textTransform: "capitalize" } },
  },
}));

const Department = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false); // Separate loading for data fetch
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [departmentsDataCount, setDepartmentsDataCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formValues, setFormValues] = useState({
    departmentName: "",
    departmentValue: "",
  });

  // Create debounced function using useCallback and useRef
  const debouncedSearch = useRef(
    debounce((value) => {
      setDebouncedSearchText(value);
      setPage(0); // Reset to first page on new search
    }, 500)
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const allDepartments = useCallback(() => {
    setDataLoading(true);
    let params = {
      page: `${page + 1}`,
      limit: rowsPerPage,
      search: debouncedSearchText,
      sortBy: sortColumn,
      sortOrder: sortDirection,
    };
    getAllDepartment(params)
      .then((res) => {
        setDataLoading(false);
        if (res.status === 200) {
          if (res?.data?.data?.length === 0) {
            setDepartmentsData([]);
          } else {
            setDepartmentsData(res?.data?.data);
            setDepartmentsDataCount(res?.data?.departmentsDataCount);
          }
        }
      })
      .catch((error) => {
        setDataLoading(false);
        // generateErrorMessage(`${error?.response?.data?.message || ""}`);
      });
  }, [page, rowsPerPage, debouncedSearchText, sortColumn, sortDirection]);

  useEffect(() => {
    allDepartments();
  }, [allDepartments]);

  useEffect(() => {
    const { hostname } = window.location;
    document.title = `${hostname} | Departments `;
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleDeleteClick = (department) => {
    setSelectedDepartment(department);
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDepartment) return;

    try {
      setLoading(true);
      await deleteDepartment(selectedDepartment.depId);
      generateSuccessMessage("Department deleted successfully");
      await allDepartments();
      setConfirmModalOpen(false);
      setSelectedDepartment(null);
    } catch (err) {
      generateErrorMessage(
        err?.response?.data?.message || "Failed to delete department"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedDepartment(null);
    setFormValues({
      departmentName: "",
      departmentValue: "",
    });
    setFormMode("add");
    setDialogOpen(true);
  };

  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setFormValues({
      departmentName: department.departmentName,
      departmentValue: department.departmentValue,
    });
    setFormMode("edit");
    setDialogOpen(true);
  };

  const handleFormSubmit = () => {
    setFormSubmitted(true);

    const isValid =
      formValues.departmentName.trim() && formValues.departmentValue.trim();

    if (!isValid) return;

    const payload = {
      departmentName: formValues.departmentName,
      departmentValue: formValues.departmentValue,
    };

    setLoading(true);

    const request =
      formMode === "edit"
        ? updateDepartment(selectedDepartment.depId, payload)
        : createDepartment(payload);

    request
      .then((res) => {
        setDialogOpen(false);
        allDepartments();
        generateSuccessMessage(res?.data?.message || "");
      })
      .catch((err) => {
        generateErrorMessage(err?.response?.data?.message || "");
      })
      .finally(() => {
        setLoading(false);
        setFormSubmitted(false);
      });
  };

  return (
    <Container>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <TextField
            label="Search Department"
            variant="standard"
            size="small"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              endAdornment: dataLoading ? (
                <CircularProgress size={20} />
              ) : null,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            startIcon={<AddIcon />}
          >
            Add Department
          </Button>
        </Box>

        <Box width="100%" overflow="auto">
          {departmentsData?.length > 0 ? (
            <Box width="100%" overflow="auto">
              <TableContainer>
                <StyledTable sx={{ minWidth: 870 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">ID</TableCell>
                      <TableCell align="center">Name</TableCell>
                      <TableCell align="center">Created Date</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentsData?.map((department, index) => (
                      <TableRow key={`${department?.depId || index}`}>
                        <TableCell align="center">
                          {department?.departmentName || "_"}
                        </TableCell>
                        <TableCell align="center">
                          {department?.departmentValue || "_"}
                        </TableCell>
                        <TableCell align="center">
                          {convertedDate(department?.createdAt) || "_"}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleEdit(department)}
                            color="primary"
                            disabled={dataLoading}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(department)}
                            color="error"
                            disabled={dataLoading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>

              <TablePagination
                className="pagination-container"
                classes={{
                  select: "pagination-select",
                  actions: "pagination-actions",
                }}
                sx={{ px: 2 }}
                page={page}
                component="div"
                rowsPerPage={rowsPerPage}
                count={departmentsDataCount}
                onPageChange={handleChangePage}
                rowsPerPageOptions={[10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          ) : (
            !dataLoading && (
              <p
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                No data available!
              </p>
            )
          )}
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {formMode === "edit" ? "Edit Department" : "Add Department"}
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Department Name"
              variant="outlined"
              fullWidth
              value={formValues.departmentName}
              onChange={(e) =>
                setFormValues({ ...formValues, departmentName: e.target.value })
              }
              error={formSubmitted && !formValues.departmentName.trim()}
              helperText={
                formSubmitted && !formValues.departmentName.trim()
                  ? "Department Name is required"
                  : ""
              }
            />

            <TextField
              label="Department Value"
              variant="outlined"
              fullWidth
              value={formValues.departmentValue}
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  departmentValue: e.target.value,
                })
              }
              error={formSubmitted && !formValues.departmentValue.trim()}
              helperText={
                formSubmitted && !formValues.departmentValue.trim()
                  ? "Department Value is required"
                  : ""
              }
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            color="primary"
            disabled={
              !formValues.departmentName.trim() ||
              !formValues.departmentValue.trim()
            }
          >
            {formMode === "edit" ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setSelectedDepartment(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Department?"
        description="Are you sure you want to delete this department? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />

      <BackdropLoader loader={loading} />
    </Container>
  );
};

export default Department;